import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { practiceService } from '@/core/services/practice';
import { notifyError } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import type { SideListItem } from '@/core/models/practice.models';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import './SideList.scss';

interface SideListProps {
  /** Angular `@Output() toggleSideList`: emits `closeMenu` after each toggle (no host binds it). */
  onToggleSideList?: (closed: boolean) => void;
}

/**
 * The collapsible "Clients" list of the Add Client, Add User and firm chart-of-accounts screens (Angular
 * `app-side-list`). Quirks kept on purpose: it starts COLLAPSED, the search is a native `window.prompt`, the add
 * button always goes to Add Client (whatever screen hosts the list), and a client opens through `item.id`. The
 * ticked ids are tracked but nothing consumes them.
 */
export function SideList({ onToggleSideList }: SideListProps = {}) {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<SideListItem[]>([]);
  const [closeMenu, setCloseMenu] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const mounted = useRef(false);

  const load = useCallback((term: string) => {
    practiceService.getClientSideList(1, 500, term).then(
      (response) => {
        if (!mounted.current) return;
        setDataSource(response.sideModuleList);
        setSelectedIds(new Set());
      },
      (error) => notifyError(error),
    );
  }, []);

  useEffect(() => {
    mounted.current = true;
    load('');
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const onSearchClick = () => {
    const term = window.prompt('Search clients', search);
    if (term === null) {
      return;
    }
    const next = term.trim();
    setSearch(next);
    load(next);
  };

  const onAddClick = () => {
    void navigate('/home/manage/addClient');
  };

  const onOpen = (item: SideListItem) => {
    void navigate(matrixPath('/home/manage', 'addClient', { id: btoa(item.id) }));
  };

  const isAllSelected = dataSource.length > 0 && dataSource.every((item) => selectedIds.has(item.id));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(new Set(checked ? dataSource.map((item) => item.id) : []));
  };

  const toggleRow = (item: SideListItem, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(item.id);
      } else {
        next.delete(item.id);
      }
      return next;
    });
  };

  const menuToggle = () => {
    const closed = !closeMenu;
    setCloseMenu(closed);
    onToggleSideList?.(closed);
    window.dispatchEvent(new Event('resize'));
  };

  const columns: DataTableColumn<SideListItem>[] = [
    {
      id: 'name',
      cell: (element) => (
        <div className="list-wrapper">
          <div className="d-flex align-items-center">
            <Checkbox
              checked={selectedIds.has(element.id)}
              onChange={(event) => toggleRow(element, event.target.checked)}
            />
            <a
              href="#"
              className="list-name clamp"
              onClick={(event) => {
                event.preventDefault();
                onOpen(element);
              }}
            >
              {element.name}
            </a>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="side-list-host">
      <div className="d-flex h-100">
        <div className={`side-list ${closeMenu ? 'hide-sidelist' : ''}`.trim()}>
          <div className="side-list-wrapper h-100">
            <div className="d-flex justify-space-between align-items-center side-list-header">
              <div className="d-flex align-items-center">
                <Checkbox checked={isAllSelected} onChange={(event) => toggleAll(event.target.checked)} />

                <span className="clamp side-list-header-text">Clients</span>
              </div>
              <div className="side-list-icon">
                <Button className="search" aria-label="Search clients" title="Search clients" onClick={onSearchClick}>
                  <Icon name="search" />
                </Button>
                <Button className="edit-add-form" aria-label="Add new client" title="Add new client" onClick={onAddClick}>
                  <Icon name="add_circle" />
                </Button>
              </div>
            </div>
            <div className="mat-table-wrapper">
              <DataTable columns={columns} rows={dataSource} rowKey={(item) => item.id} stickyHeader />
            </div>
          </div>
        </div>
      </div>
      <div className={`menu-toggle ${closeMenu ? 'toggle-icon' : ''}`.trim()} onClick={menuToggle}>
        <Icon name={closeMenu ? 'keyboard_arrow_right' : 'keyboard_arrow_left'} />
      </div>
    </div>
  );
}
